/**
 * OrderContextPanel - Shared component for displaying order context in ticket modals
 * 
 * Shows:
 * - Property address
 * - Host information
 * - Guest information  
 * - Order story/timeline
 * - Completed tickets for this order
 */

import React, { useState, useEffect } from 'react';
import {
  FiHome, FiUser, FiUsers, FiClock, FiCheck, FiPackage, FiTruck,
  FiCalendar, FiMapPin, FiMail, FiPhone, FiChevronDown, FiChevronRight,
  FiMessageSquare, FiDollarSign, FiAlertCircle, FiActivity
} from 'react-icons/fi';
import request from '../../utils/request';

// Format raw MongoDB timestamp - show as-is from database
const formatRawTime = (dateString) => {
  if (!dateString) return 'Not set';
  return dateString; // Return raw ISO string from MongoDB
};

// Format date/time for display (simple)
const formatDateTime = (dateString) => {
  if (!dateString) return 'Not set';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return dateString;
  }
};

// Format just the date
const formatDate = (dateString) => {
  if (!dateString) return 'Not set';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
};

// Format just the time
const formatTime = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
};

// Legacy exports for compatibility
const formatToPST = formatDateTime;
const formatDatePST = formatDate;
const formatTimePST = formatTime;

export { formatToPST, formatDatePST, formatTimePST, formatRawTime, formatDateTime, formatDate, formatTime };

export default function OrderContextPanel({ order, ticket, compact = false }) {
  const [expanded, setExpanded] = useState(!compact);
  const [orderTickets, setOrderTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);

  // Fetch tickets for this order
  useEffect(() => {
    if (!order?._id) return;
    
    const fetchOrderTickets = async () => {
      setLoadingTickets(true);
      try {
        const res = await request.get(`/tickets/order/${order._id}`);
        if (res.data?.tickets) {
          setOrderTickets(res.data.tickets);
        }
      } catch (err) {
        console.error('Error fetching order tickets:', err);
      } finally {
        setLoadingTickets(false);
      }
    };
    
    fetchOrderTickets();
  }, [order?._id]);

  if (!order) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
        <FiAlertCircle className="w-4 h-4 inline mr-2" />
        No order data available
      </div>
    );
  }

  // Extract property info - Order has propertyID which refs Property
  const property = order.property || order.propertyID || {};
  const address = property.address || {};
  
  // Build full address from property.address (line1, line2, city, state, zipCode)
  let fullAddress = 'No address on file';
  if (address.line1) {
    fullAddress = [
      address.line1,
      address.line2,
      address.city,
      address.state,
      address.zipCode
    ].filter(Boolean).join(', ');
  } else if (property.description) {
    fullAddress = property.description;
  }

  // Extract host info - Host is linked through Property (property.hostID)
  // The backend populates: order.propertyID.hostID
  const host = property.hostID || property.host || order.host || {};
  
  // Extract guest info from order
  const guest = {
    email: order.guestEmail,
    name: order.guestName || order.guestFirstName,
    phone: order.guestPhone,
  };
  
  // Entry instructions can come from order or property
  const entryInstructions = order.entryInstructions || property.description || property.keyInfo;

  // Build order timeline/story
  const timeline = buildOrderTimeline(order);
  
  // Completed tickets
  const completedTickets = orderTickets.filter(t => t.status === 'completed');

  if (compact) {
    return (
      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between p-3 text-left"
        >
          <div className="flex items-center gap-2">
            <FiActivity className="w-4 h-4 text-indigo-500" />
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Order Context</span>
            <span className="text-xs text-slate-500">#{order.uniqueId}</span>
          </div>
          {expanded ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
        </button>
        {expanded && <div className="px-3 pb-3"><OrderContextContent {...{ order, property, fullAddress, host, guest, timeline, completedTickets, loadingTickets, entryInstructions }} /></div>}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <OrderContextContent {...{ order, property, fullAddress, host, guest, timeline, completedTickets, loadingTickets, entryInstructions }} />
    </div>
  );
}

function OrderContextContent({ order, property, fullAddress, host, guest, timeline, completedTickets, loadingTickets, entryInstructions }) {
  return (
    <div className="space-y-4">
      {/* Key Dates - Raw MongoDB times */}
      <ContextSection title="Delivery Schedule" icon={FiCalendar}>
        <div className="space-y-2">
          <InfoBox label="In-Stock Date (Raw)" value={order.inStockDate || 'Not set'} highlight />
          {order.hostSetDeliveryTime && (
            <InfoBox label="Host Set Delivery Time" value={order.hostSetDeliveryTime} />
          )}
          {order.storeArrivalTime && (
            <InfoBox label="Store Arrival Time" value={order.storeArrivalTime} />
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg">
            <span className="text-slate-500">Created:</span>
            <p className="text-slate-700 dark:text-slate-300 font-mono">{order.createdAt || '-'}</p>
          </div>
          <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg">
            <span className="text-slate-500">Updated:</span>
            <p className="text-slate-700 dark:text-slate-300 font-mono">{order.updatedAt || '-'}</p>
          </div>
        </div>
      </ContextSection>

      {/* Property Address */}
      <ContextSection title="Property" icon={FiHome}>
        <div className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0">
              <FiMapPin className="w-5 h-5 text-violet-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 dark:text-white text-sm">
                {property.listingTitle || property.name || 'Property'}
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">
                {fullAddress}
              </p>
              {entryInstructions && (
                <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
                    <strong>Entry:</strong> {entryInstructions}
                  </p>
                </div>
              )}
              {property.keyInfo && property.keyInfo !== entryInstructions && (
                <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-xs font-medium text-blue-700 dark:text-blue-300">
                    <strong>Key Info:</strong> {property.keyInfo}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </ContextSection>

      {/* Host Info */}
      <ContextSection title="Host" icon={FiUser}>
        <div className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <FiUser className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-slate-900 dark:text-white text-sm">
                {host.firstName || host.lastName ? `${host.firstName || ''} ${host.lastName || ''}`.trim() : 'No host info'}
              </p>
              <div className="flex flex-col gap-1 mt-1 text-xs text-slate-500">
                {host.email && (
                  <span className="flex items-center gap-1">
                    <FiMail className="w-3 h-3" /> {host.email}
                  </span>
                )}
                {(host.phoneNumber || host.phone) && (
                  <span className="flex items-center gap-1">
                    <FiPhone className="w-3 h-3" /> {host.phoneNumber || host.phone}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </ContextSection>

      {/* Guest Info */}
      <ContextSection title="Guest" icon={FiUsers}>
        <div className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
              <FiUsers className="w-5 h-5 text-sky-600" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-slate-900 dark:text-white text-sm">
                {guest.name || 'Guest'}
              </p>
              <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                {guest.email && (
                  <span className="flex items-center gap-1">
                    <FiMail className="w-3 h-3" /> {guest.email}
                  </span>
                )}
                {guest.phone && (
                  <span className="flex items-center gap-1">
                    <FiPhone className="w-3 h-3" /> {guest.phone}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </ContextSection>

      {/* Order Timeline/Story */}
      <ContextSection title="Order Story" icon={FiActivity}>
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700" />
          <div className="space-y-3">
            {timeline.map((event, idx) => (
              <TimelineEvent key={idx} event={event} isLast={idx === timeline.length - 1} />
            ))}
          </div>
        </div>
      </ContextSection>

      {/* Completed Tickets */}
      <ContextSection title="Completed Tasks" icon={FiCheck}>
        {loadingTickets ? (
          <p className="text-sm text-slate-500">Loading...</p>
        ) : completedTickets.length === 0 ? (
          <p className="text-sm text-slate-500">No completed tickets yet</p>
        ) : (
          <div className="space-y-2">
            {completedTickets.map(t => (
              <CompletedTicketRow key={t._id} ticket={t} />
            ))}
          </div>
        )}
      </ContextSection>

      {/* Order Summary Stats */}
      <ContextSection title="Order Summary" icon={FiDollarSign}>
        <div className="grid grid-cols-3 gap-2">
          <StatBox label="Items" value={order.products?.length || 0} />
          <StatBox label="Store Spend" value={order.storeSpend ? `$${order.storeSpend.toFixed(2)}` : '-'} />
          <StatBox label="Status" value={order.status || 'Unknown'} />
        </div>
      </ContextSection>
    </div>
  );
}

function ContextSection({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function InfoBox({ label, value, highlight }) {
  return (
    <div className={`p-3 rounded-xl ${highlight ? 'bg-indigo-50 dark:bg-indigo-900/20' : 'bg-slate-100 dark:bg-slate-800'}`}>
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-sm font-semibold mt-0.5 ${highlight ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-900 dark:text-white'}`}>
        {value}
      </p>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-center">
      <p className="text-lg font-bold text-slate-900 dark:text-white">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}

function TimelineEvent({ event, isLast }) {
  const iconColor = {
    created: 'bg-slate-400',
    payment: 'bg-emerald-500',
    placed: 'bg-indigo-500',
    driver: 'bg-violet-500',
    delivery: 'bg-amber-500',
    completed: 'bg-green-500',
    support: 'bg-rose-500',
  };

  return (
    <div className="relative flex items-start gap-3 pl-8">
      <div className={`absolute left-2.5 w-3 h-3 rounded-full ${iconColor[event.type] || 'bg-slate-400'} ring-4 ring-white dark:ring-slate-900`} />
      <div className="flex-1 pb-3">
        <p className="text-sm font-medium text-slate-900 dark:text-white">{event.title}</p>
        {event.description && <p className="text-xs text-slate-500 mt-0.5">{event.description}</p>}
        <p className="text-xs text-slate-400 mt-1">{formatToPST(event.timestamp)}</p>
        {event.user && (
          <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded-full text-xs text-slate-600 dark:text-slate-400">
            <FiUser className="w-3 h-3" /> {event.user}
          </span>
        )}
      </div>
    </div>
  );
}

function CompletedTicketRow({ ticket }) {
  return (
    <div className="flex items-center justify-between p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
      <div className="flex items-center gap-2">
        <FiCheck className="w-4 h-4 text-emerald-600" />
        <span className="text-sm font-medium text-slate-900 dark:text-white">{ticket.name || ticket.ticketTypeId}</span>
      </div>
      <div className="text-xs text-slate-500">
        {ticket.completedByName || 'Unknown'} • {formatToPST(ticket.completedAt, { year: undefined, month: 'short', day: 'numeric' })}
      </div>
    </div>
  );
}

// Build timeline from order data
function buildOrderTimeline(order) {
  const events = [];

  // Order created
  if (order.createdAt) {
    events.push({
      type: 'created',
      title: 'Order Created',
      timestamp: order.createdAt,
    });
  }

  // Payment completed
  if (order.paymentCompletedAt || order.status === 'paymentCompleted') {
    events.push({
      type: 'payment',
      title: 'Payment Completed',
      timestamp: order.paymentCompletedAt || order.createdAt,
    });
  }

  // Order placed at store
  if (order.storeOrderId || order.storeOrderConfirmationUrl) {
    events.push({
      type: 'placed',
      title: 'Order Placed at Store',
      description: order.storeOrderId ? `Order ID: ${order.storeOrderId}` : null,
      timestamp: order.placedAt || order.updatedAt,
    });
  }

  // Driver assigned
  if (order.driverID) {
    events.push({
      type: 'driver',
      title: 'Concierge Assigned',
      description: order.driverName || 'Concierge assigned',
      timestamp: order.driverAssignedAt || order.requestedAt,
    });
  }

  // Order started by driver
  if (order.orderStarted || order.orderStartedAt) {
    events.push({
      type: 'driver',
      title: 'Concierge Started Order',
      timestamp: order.orderStartedAt,
    });
  }

  // Picked up
  if (order.status === 'picked' || order.pickedAt) {
    events.push({
      type: 'delivery',
      title: 'Order Picked Up',
      timestamp: order.pickedAt || order.updatedAt,
    });
  }

  // Delivered
  if (order.status === 'delivered' || order.deliveredAt) {
    events.push({
      type: 'completed',
      title: 'Order Delivered',
      timestamp: order.deliveredAt || order.updatedAt,
    });
  }

  // EOD Review
  if (order.eodReview?.reviewedAt) {
    events.push({
      type: 'support',
      title: 'EOD Review Completed',
      user: order.eodReview.reviewedByName,
      timestamp: order.eodReview.reviewedAt,
    });
  }

  // Morning Check
  if (order.morningCheck?.checkedAt) {
    events.push({
      type: 'support',
      title: 'Morning Check Completed',
      user: order.morningCheck.checkedByName,
      timestamp: order.morningCheck.checkedAt,
    });
  }

  // Sort by timestamp
  return events.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
}

